import { useState } from 'react';

import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import NoContent from '../../sections/no-content/NoContent';

/**
 * Compares two objects in descending order based on a given key.
 *
 * @template T
 * @param {T} a - The first object to compare.
 * @param {T} b - The second object to compare.
 * @param {keyof T} orderBy - The key to sort by.
 * @returns {number} - Returns -1 if `a` should come before `b`, 1 if after, or 0 if equal.
 */
const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

/**
 * Returns a comparator function for sorting objects based on the given order and key.
 *
 * @template T
 * @param {'asc' | 'desc'} order - The sorting order. `'asc'` for ascending, `'desc'` for descending.
 * @param {keyof T} orderBy - The key to sort by.
 * @returns {(a: T, b: T) => number} - A comparator function usable in `Array.prototype.sort`.
 */
const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const Table = ({ headers, rows, small, emptyText }) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null); // which column is sorted

  const handleOrder = (headerName) => {
    const isAsc = orderBy === headerName && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(headerName);
  };

  const visibleRows = [...rows].sort(getComparator(order, orderBy));

  return isEmpty(rows) ? (
    <NoContent title={emptyText} />
  ) : (
    <TableContainer>
      <MuiTable size={small ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {Object.entries(headers).map(([key, header], i) => (
              <TableCell
                key={key}
                sortDirection={orderBy === key ? order : false}
                align={i + 1 > 1 ? 'center' : 'left'}
              >
                {header.sortable ? (
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : 'asc'}
                    onClick={() => handleOrder(key)}
                  >
                    {header.name}
                  </TableSortLabel>
                ) : (
                  header.name
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row, i) => (
            <TableRow key={i}>
              {Object.values(row).map((item, j) => (
                <TableCell key={j} align={j + 1 > 1 ? 'center' : 'left'}>
                  {item}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

Table.propTypes = {
  headers: PropTypes.object.isRequired,
  rows: PropTypes.array.isRequired,
  small: PropTypes.bool,
  emptyText: PropTypes.string.isRequired,
};

export default Table;
